import { useEffect, useRef, useState } from 'react'
import type { User } from 'oidc-client-ts'
import { clearAuthTokenCookies, setAuthTokenToCookies } from 'api/auth'
import { reportClientError } from 'services/errorReportingService'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { ACCOUNT_INIT_COMPLETE, API_CALL_SUCCESS, SET_ACCESS_TOKEN } from 'actions/actionConstants'
import { USER_LOGOUT } from 'actions/apiActionConstants'
import { registerAuthTokenRefreshHandler, unregisterAuthTokenRefreshHandler } from 'api/authTokenRefresh'
import {
  clearOidcTokenListener,
  ensureValidOidcSession,
  getOidcUserManager,
  setOidcTokenListener,
} from './ensureValidOidcSession'
import { createSilentRenewer, isTerminalAuthError } from './silentRenew'
import { recoverOidcSession } from './recoverOidcSession'
import { clearAuthMethodMarker, isOidcSession, OIDC_USER_STORAGE_PREFIX } from './authMethod'

// App-wide owner of a v2 (OIDC) session: recovery, renewal and token mirroring.
// Returns true while restoring, for a blocking overlay. No-op for v1.
// See docs/core/auth/agent-hub-oidc-session-renewal.md.
export default function useOidcSessionCoordinator(): boolean {
  const [isMounted, setIsMounted] = useState(false)
  const [isRestoringSession, setIsRestoringSession] = useState(false)
  const dispatch = useAppDispatch()
  // A ref so the effects don't re-run (re-creating listeners) when login flips true.
  const loggedInRef = useRef(false)
  loggedInRef.current = useAppSelector(selectLoggedIn)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (!isMounted || !isOidcSession()) {
      return
    }

    let disposed = false
    let detach: () => void = () => undefined
    registerAuthTokenRefreshHandler(ensureValidOidcSession)

    const mirrorToken = (user: User) => {
      if (user.access_token) {
        setAuthTokenToCookies(user.access_token)
        dispatch({ type: SET_ACCESS_TOKEN, accessToken: user.access_token })
      }
    }

    // Covers the paths userLoaded does not: adopting a token another tab rotated,
    // and reusing a still-fresh one. The cookie is already written by then.
    const mirrorSettledToken = (accessToken: string) => {
      dispatch({ type: SET_ACCESS_TOKEN, accessToken })
    }

    setOidcTokenListener(mirrorSettledToken)

    const discardStoredSession = async() => {
      const userManager = await getOidcUserManager()
      await userManager.removeUser().catch(() => undefined)
      clearAuthMethodMarker()
      clearAuthTokenCookies()
    }

    const renewer = createSilentRenewer({
      renew: ensureValidOidcSession,
      onTransientError: (error) => {
        reportClientError(error, { context: 'oidcSilentRenewRetry' }).catch(() => undefined)
      },
      // No endOidcSession: svc-auth has already revoked it, so there is no
      // upstream session left to end.
      onSessionEnded: async() => {
        await discardStoredSession()
        // supressBroadcast, or userMiddleware's `'supressBroadcast' in action`
        // gate is false and the other tabs never hear about the revocation.
        dispatch({ type: API_CALL_SUCCESS, api: USER_LOGOUT, supressBroadcast: false })
      },
    })

    const setup = async() => {
      try {
        const userManager = await getOidcUserManager()
        if (disposed) {
          return
        }
        userManager.events.addUserLoaded(mirrorToken)
        // No silentRenewError listener: only SilentRenewService raises it, and
        // automaticSilentRenew is off.
        userManager.events.addAccessTokenExpiring(renewer.renew)
        userManager.events.addAccessTokenExpired(renewer.renew)
        detach = () => {
          userManager.events.removeUserLoaded(mirrorToken)
          userManager.events.removeAccessTokenExpiring(renewer.renew)
          userManager.events.removeAccessTokenExpired(renewer.renew)
        }

        // Also arms the expiry timers; nothing else does.
        const user = await userManager.getUser()
        if (user) {
          mirrorToken(user)
        }

        if (loggedInRef.current) {
          return
        }

        setIsRestoringSession(true)
        try {
          await dispatch(recoverOidcSession())
        } catch(error) {
          reportClientError(error, { context: 'oidcSessionRecover' }).catch(() => undefined)

          // Only clear on a refusal. A blip here used to delete a working
          // refresh token, turning one failed /me into a permanent logout.
          if (isTerminalAuthError(error)) {
            await discardStoredSession()
          }

          dispatch({ type: ACCOUNT_INIT_COMPLETE })
        } finally {
          if (!disposed) {
            setIsRestoringSession(false)
          }
        }
      } catch(error) {
        // Setup failed. initialiseUser defers init for the OIDC path, so settle it
        // here or the app hangs uninitialised.
        reportClientError(error, { context: 'oidcSessionCoordinator' }).catch(() => undefined)
        if (!disposed) {
          setIsRestoringSession(false)
        }
        dispatch({ type: ACCOUNT_INIT_COMPLETE })
      }
    }

    // getUser(true): the raiseEvent default is false, which arms the expiry
    // timers but never raises userLoaded — so the rotated token reached neither
    // the cookie nor Redux, and this tab kept sending the one it replaced.
    const onStorage = (event: StorageEvent) => {
      if (event.key?.includes(OIDC_USER_STORAGE_PREFIX)) {
        getOidcUserManager()
          .then((userManager) => userManager.getUser(true))
          .catch(() => undefined)
      }
    }

    // A frozen background tab runs no timers, so neither expiry event may have
    // fired while it was idle. pageshow covers a bfcache restore.
    const onForeground = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return
      }
      // Bootstrap owns the logged-out case.
      if (!loggedInRef.current) {
        return
      }
      renewer.renew()
    }

    window.addEventListener('storage', onStorage)
    document.addEventListener('visibilitychange', onForeground)
    window.addEventListener('pageshow', onForeground)
    setup()

    return () => {
      disposed = true
      clearOidcTokenListener(mirrorSettledToken)
      renewer.dispose()
      detach()
      window.removeEventListener('storage', onStorage)
      document.removeEventListener('visibilitychange', onForeground)
      window.removeEventListener('pageshow', onForeground)
      unregisterAuthTokenRefreshHandler(ensureValidOidcSession)
    }
  }, [isMounted, dispatch])

  return isRestoringSession
}
